import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import type { Page } from './types';

export const AUDIT_ACTIONS = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'STATUS_CHANGE',
  'FORM_SAVE',
  'FORM_DEFINITION_CHANGE',
  'FORM_TEMPLATE_PUBLISH',
  'REPORT_GENERATE',
  'LOGIN',
  'LOGOUT',
  'LOGIN_FAILED',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export interface AuditActor {
  sub: string;
  username: string | null;
  displayName: string | null;
  roles: string[];
  system: boolean;
}

export interface AuditEntry {
  id: number;
  occurredAt: string;
  actor: AuditActor;
  action: AuditAction;
  entityType: string | null;
  entityId: string | null;
  summary: string;
  hasChanges: boolean;
}

export interface AuditEntryDetail extends Omit<AuditEntry, 'hasChanges'> {
  changes: Record<string, unknown> | null;
  revisionId: number | null;
  requestId: string | null;
  clientIp: string | null;
  eventKey: string | null;
}

export interface AuditQuery {
  from?: string;
  to?: string;
  user?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  page: number;
  size: number;
  sort: string;
}

export function useAuditLog(query: AuditQuery) {
  return useQuery({
    queryKey: ['audit-log', query],
    queryFn: ({ signal }) => api<Page<AuditEntry>>('/audit-log', { query: { ...query }, signal }),
    placeholderData: (previous) => previous,
  });
}

export function useAuditEntry(id: string | undefined) {
  return useQuery({ queryKey: ['audit-log', 'entry', id], queryFn: () => api<AuditEntryDetail>(`/audit-log/${id}`), enabled: !!id });
}

export function useAuditEntityTypes() {
  return useQuery({ queryKey: ['audit-log', 'entity-types'], queryFn: () => api<string[]>('/audit-log/entity-types'), staleTime: 60_000 });
}
